import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tag, message } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  deleteCampaign,
  getCampaign,
  getCampaignRecipients,
  unscheduleCampaign,
} from 'api/campaigns';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getCampaignErrorMessage } from './errorMessages';
import { formatCampaignDateTime, renderCampaignBody } from './format';
import SuccessFailBadges from './SuccessFailBadges';
import { StyledWrapper } from './styles';

const STATUS_COLORS = {
  draft: 'default',
  scheduled: 'blue',
  sending: 'processing',
  sent: 'green',
  failed: 'red',
};

function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [recipientsSource, setRecipientsSource] = useState(null);
  const [excludedRecipients, setExcludedRecipients] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const pageSize = 20;

  const load = (opts = {}) => {
    const { quiet = false } = opts;
    if (!quiet) setLoading(true);
    getCampaign(id)
      .then((res) => {
        const data = getData(res)?.data || getData(res);
        setCampaign(data);
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        if (!quiet) {
          message.error(getCampaignErrorMessage(e, 'Could not load this campaign.'));
        }
        setLoading(false);
      });
  };

  const loadRecipients = (page = 1) => {
    setRecipientsLoading(true);
    getCampaignRecipients(id, { page, page_size: pageSize })
      .then((res) => {
        const data = getData(res)?.data || getData(res);
        setRecipients(data?.results || []);
        setRecipientsTotal(data?.count ?? 0);
        setRecipientsSource(data?.source || null);
        setExcludedRecipients(Array.isArray(data?.excluded) ? data.excluded : []);
        setExcludedCount(data?.excluded_count ?? 0);
        setRecipientsPage(page);
        setRecipientsLoading(false);
      })
      .catch((e) => {
        captureException(e);
        message.error(getCampaignErrorMessage(e, 'Could not load recipients.'));
        setRecipientsLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, [id]);

  // Poll while background send is in progress so status/results update without refresh.
  useEffect(() => {
    if (campaign?.status !== 'sending') return undefined;
    const timer = setInterval(() => load({ quiet: true }), 2500);
    return () => clearInterval(timer);
  }, [campaign?.status, id]);

  const handleUnschedule = async () => {
    setActing(true);
    try {
      await unscheduleCampaign(id);
      message.success('Returned to draft');
      load();
    } catch (e) {
      captureException(e);
      message.error(getCampaignErrorMessage(e, 'Could not unschedule campaign.'));
    } finally {
      setActing(false);
    }
  };

  const handleDelete = async () => {
    setActing(true);
    try {
      await deleteCampaign(id);
      message.success('Draft deleted');
      navigate('/console/marketing/campaigns');
    } catch (e) {
      captureException(e);
      message.error(getCampaignErrorMessage(e, 'Could not delete campaign.'));
    } finally {
      setActing(false);
    }
  };

  const toggleRecipients = () => {
    if (showRecipients) {
      setShowRecipients(false);
      return;
    }
    setShowRecipients(true);
    loadRecipients(1);
  };

  if (loading || !campaign) {
    return (
      <Layout>
        <StyledWrapper>
          <p>{loading ? 'Loading…' : 'Campaign not found'}</p>
        </StyledWrapper>
      </Layout>
    );
  }

  const recipientColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '—',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value) => {
        if (value === 'failed') return <Tag color="red">Failed</Tag>;
        if (value === 'skipped') return <Tag color="orange">Skipped</Tag>;
        if (value === 'sent') return <Tag color="green">Sent</Tag>;
        if (value === 'ready') return <Tag>Ready</Tag>;
        return '—';
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason || '—',
    },
  ];

  return (
    <Layout>
      <StyledWrapper>
        <Button
          type="link"
          className="back-link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/console/marketing/campaigns')}
        >
          Back to campaigns
        </Button>
        <div className="campaigns-header">
          <div>
            <h1 className="campaigns-title">{campaign.name}</h1>
            <p className="campaigns-subtitle">
              <Tag color={STATUS_COLORS[campaign.status] || 'default'}>
                {(campaign.status || '').charAt(0).toUpperCase() + (campaign.status || '').slice(1)}
              </Tag>
              {' '}
              {campaign.channel === 'sms' ? 'SMS' : 'Email'}
            </p>
          </div>
          <Space>
            {campaign.status === 'draft' && (
              <>
                <Button onClick={() => navigate(`/console/marketing/campaigns/${id}/edit`)}>
                  Edit
                </Button>
                <Button danger loading={acting} onClick={handleDelete}>Delete</Button>
              </>
            )}
            {campaign.status === 'scheduled' && (
              <Button loading={acting} onClick={handleUnschedule}>
                Unschedule (back to draft)
              </Button>
            )}
          </Space>
        </div>

        <div className="detail-meta">
          <div>
            <div className="label">Send at</div>
            <div className="value value-datetime">
              {formatCampaignDateTime(campaign.send_at)}
            </div>
          </div>
          <div>
            <div className="label">Recipients</div>
            <div className="value recipients-value">
              <div>
                {campaign.recipient_count ?? 0}
                {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                  <span>
                    {' '}
                    will receive this
                    {' '}
                    {campaign.channel === 'sms' ? 'SMS' : 'email'}
                  </span>
                )}
                {campaign.skipped_count > 0 && campaign.status !== 'sent' && campaign.status !== 'failed' && (
                  <div className="recipient-warning">
                    {campaign.skipped_count}
                    {' '}
                    skipped, missing
                    {' '}
                    {campaign.channel === 'sms' ? 'a phone number' : 'an email'}
                  </div>
                )}
              </div>
              <Button className="recipients-toggle" onClick={toggleRecipients}>
                {showRecipients ? 'Hide recipients' : 'Show recipients'}
              </Button>
            </div>
          </div>
          <div>
            <div className="label">Results</div>
            <div className="value">
              <SuccessFailBadges
                success={campaign.success_count}
                failure={campaign.failure_count}
                skipped={campaign.skipped_count}
                status={campaign.status}
              />
              {campaign.error_message && (
                <div className="result-reason">{campaign.error_message}</div>
              )}
              {!campaign.error_message && campaign.failure_count > 0 && (
                <div className="result-reason">
                  A recipient send failed. The reason was not recorded for this campaign.
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="label">Coupon</div>
            <div className="value">
              {campaign.coupon_detail
                ? `${campaign.coupon_detail.name} (${campaign.coupon_detail.code})`
                : '—'}
            </div>
          </div>
          <div>
            <div className="label">Segments</div>
            <div className="value">
              {(campaign.segments || []).map((s) => s.name).join(', ') || '—'}
            </div>
          </div>
          <div>
            <div className="label">Extra customers</div>
            <div className="value">
              {(campaign.extra_customers || []).map((c) => c.email || c.name).join(', ') || '—'}
            </div>
          </div>
        </div>

        {campaign.channel === 'email' && (
          <div className="form-section">
            <h3>Subject</h3>
            <div className="message-preview">{campaign.subject}</div>
          </div>
        )}

        <div className="form-section">
          <h3>Message</h3>
          <div className="message-preview">
            {renderCampaignBody(campaign.body, campaign.coupon_detail?.code)}
          </div>
        </div>

        {showRecipients && (
          <div className="form-section">
            <h3>
              {recipientsSource === 'sent' ? 'Delivery results' : 'Recipients'}
            </h3>
            <Table
              rowKey="id"
              loading={recipientsLoading}
              columns={recipientColumns}
              dataSource={recipients}
              pagination={{
                current: recipientsPage,
                pageSize,
                total: recipientsTotal,
                onChange: (page) => loadRecipients(page),
              }}
            />
            {excludedCount > 0 && (
              <>
                <h3 style={{ marginTop: 24 }}>
                  Skipped (
                  {excludedCount}
                  {' '}
                  missing
                  {' '}
                  {campaign.channel === 'sms' ? 'a phone number' : 'an email'}
                  )
                </h3>
                <Table
                  rowKey="id"
                  size="small"
                  columns={recipientColumns}
                  dataSource={excludedRecipients}
                  pagination={false}
                />
              </>
            )}
          </div>
        )}
      </StyledWrapper>
    </Layout>
  );
}

export default withGuard(CampaignDetail, [PERMISSIONS.MARKETING], { redirect: true });
